// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getRemoteConfig } from "firebase/remote-config";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const auth = getAuth(app);
const db = getFirestore(app);

// Remote Config to Store the Admin Configurations
const remoteConfig = getRemoteConfig(app);
remoteConfig.settings.minimumFetchIntervalMillis = 0;
// Same bound services/remoteConfig.js sets on this instance; set here too so a
// screen that fetches before that init runs doesn't wait the SDK's 60s.
remoteConfig.settings.fetchTimeoutMillis = 8000;

/*
 * WHAT THE UPTIME SCREENS READ WHEN REMOTE CONFIG CAN'T BE REACHED.
 *
 * With no default, a failed fetch on a fresh load (offline, a 429 backoff, a
 * 5xx) made getValue return '' and every JSON.parse throw: Edit and Duplicate
 * Domain sat on their skeleton for good, and Domains turned live status off.
 * These are the production template's values (seoweb-408ea, read 2026-10-09),
 * so a failed fetch behaves exactly like a successful one. A fetched value
 * always wins over these.
 */
const UPTIME_REMOTE_CONFIG_DEFAULTS = {
    uptime_monitorRegions: JSON.stringify([
        { label: 'Australia - Sydney', country: 'au', city: 'sydney' },
        { label: 'Canada - Toronto', country: 'ca', city: 'toronto' },
        { label: 'China - Beijing', country: 'cn', city: 'beijing' },
        { label: 'China - NanChang', country: 'cn', city: 'nanchang' },
        { label: 'China - Changchun', country: 'cn', city: 'changchun' },
        { label: 'China - Changsha', country: 'cn', city: 'changsha' },
        { label: 'China - Chongqing', country: 'cn', city: 'chongqing' },
        { label: 'China - Fuzhou', country: 'cn', city: 'fuzhou' },
        { label: 'China - Fuzhou10', country: 'cn', city: 'fuzhou10' },
        { label: 'China - Fuzhou11', country: 'cn', city: 'fuzhou11' },
        { label: 'China - Guiyang', country: 'cn', city: 'guiyang' },
        { label: 'China - Hefei', country: 'cn', city: 'hefei' },
        { label: 'China - Hohhot', country: 'cn', city: 'huhehaote' },
        { label: 'China - Hong Kong', country: 'cn', city: 'hk' },
        { label: 'China - Mianyang10', country: 'cn', city: 'mianyang10' },
        { label: 'China - Nanning', country: 'cn', city: 'nanning' },
        { label: 'China - Quanzhou', country: 'cn', city: 'quanzhou' },
        { label: 'China - Shanghai', country: 'cn', city: 'shanghai' },
        { label: 'China - Tianjin', country: 'cn', city: 'tianjin' },
        { label: 'China - Wuxi', country: 'cn', city: 'wuxi' },
        { label: "China - Xi'an", country: 'cn', city: 'xian' },
        { label: 'China - Xuzhou', country: 'cn', city: 'xuzhou' },
        { label: 'China - Yantai', country: 'cn', city: 'yantai' },
        { label: 'China - Zhengzhou', country: 'cn', city: 'zhengzhou' },
        { label: 'France - Paris', country: 'fr', city: 'paris' },
        { label: 'Germany - Frankfurt', country: 'de', city: 'frankfurt' },
        { label: 'India - Delhi', country: 'in', city: 'delhi' },
        { label: 'Japan - Tokyo', country: 'jp', city: 'tokyo' },
        { label: 'Netherlands - Amsterdam', country: 'nl', city: 'amsterdam' },
        { label: 'Singapore - Singapore', country: 'sg', city: 'sg' },
        { label: 'South Africa - Johannesburg', country: 'za', city: 'jnb' },
        { label: 'Sweden - Stockholm', country: 'se', city: 'stockholm' },
        { label: 'United Arab Emirates - Dubai', country: 'ae', city: 'dubai' },
        { label: 'United Kingdom - London', country: 'uk', city: 'london' },
        { label: 'USA - Los Angeles', country: 'us', city: 'la' },
    ]),
    uptime_defaultMonitorRegions: JSON.stringify([{ label: 'Default (Auto-Select)', country: 'us', city: 'la' }]),
    uptime_httpMethods: JSON.stringify([
        { label: 'HEAD', method: 'head' },
        { label: 'GET', method: 'get' },
        { label: 'OPTIONS', method: 'options' },
    ]),
    uptime_defaultHttpMethods: JSON.stringify({ label: 'HEAD', method: 'head' }),
    uptime_upResponseCodes: JSON.stringify(['2xx', '3xx', '4xx', '5xx', '6xx']),
    uptime_defaultUpResponseCodes: JSON.stringify(['2xx', '3xx']),
    uptime_monitorInterval: '300',
    uptime_monitorThreshold: '1',
    uptime_monitorPaused: 'false',
    uptime_notifyEmail: 'true',
    uptime_defaultRequestTimeout: '60',
    uptime_defaultAlertsPerIncident: '1',
    uptime_alertsPerIncident_max: '10',
    aiwriter_cmsDefaultPlatforms: JSON.stringify(['Wordpress', 'Drupal', 'Shopify']),
};

/*
 * KEPT UNDER WHATEVER ELSE IS ASSIGNED. getRemoteConfig(app) is one instance per
 * app, and services/remoteConfig.js assigns its own `defaultConfig` to it at
 * start-up — a plain assignment would drop the uptime keys above. So an
 * assignment merges over them instead of replacing them.
 */
let remoteConfigDefaults = { ...UPTIME_REMOTE_CONFIG_DEFAULTS };
Object.defineProperty(remoteConfig, 'defaultConfig', {
    configurable: true,
    enumerable: true,
    get: () => remoteConfigDefaults,
    set: (value) => { remoteConfigDefaults = { ...UPTIME_REMOTE_CONFIG_DEFAULTS, ...(value || {}) }; },
});

export { app, analytics, auth, db, remoteConfig };
